/**
 * AUTH-002 — browser wiring for the session policy.
 *
 * One controller per tab, created lazily on first use and registered in
 * `controller-registry.ts`, where `client.tsx` reads it (the client does not import this
 * file, so there is no import cycle at load time).
 *
 * `startSessionRuntime()` is called by AuthContext once a session exists, and
 * `stopSessionRuntime()` on unmount. Starting attaches: input listeners (shared
 * last-activity), the 15 s tick (idle + keep-alive refresh, with sleep detection), and
 * the wake listeners (visibilitychange, focus, pageshow, online).
 */
import { createClient } from '@/lib/supabase/client';
import { createActivityTracker, type ActivityTracker } from './activity';
import { createAuthFetch, type AuthFetch } from './auth-fetch';
import { setActiveController } from './controller-registry';
import {
  createSessionController,
  type AuthLike,
  type SessionController,
} from './session-controller';
import {
  ACTIVITY_EVENTS,
  AUTH_NOTICE_EVENT,
  IDLE_TIMEOUT_MS,
  PHI_NOT_ENABLED_EVENT,
  SESSION_TICK_MS,
} from './session-policy';
import { clearStoredTokens } from './token-cookies';

let controller: SessionController | null = null;
let activity: ActivityTracker | null = null;
let detach: (() => void) | null = null;

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function ensureController(): { controller: SessionController; activity: ActivityTracker } | null {
  if (typeof window === 'undefined') return null;
  if (controller && activity) return { controller, activity };
  activity = createActivityTracker({ store: safeLocalStorage(), now: Date.now });
  const supabase = createClient();
  controller = createSessionController({
    auth: supabase.auth as unknown as AuthLike,
    activity,
    now: Date.now,
    clearTokens: clearStoredTokens,
    navigate: (url) => window.location.replace(url),
    currentPath: () => `${window.location.pathname}${window.location.search}`,
    // Messages carry error text only — never tokens or PHI.
    warn: (m) => console.warn(m),
  });
  setActiveController(controller);
  return { controller, activity };
}

/**
 * Begin managing the signed-in session in this tab.
 *
 * `freshSignIn` is true right after the user typed their password: that IS activity.
 * On a page load with an existing session, the shared last-activity is checked first:
 * a browser reopened (or a laptop woken) more than 15 minutes after the last input in
 * any tab signs out instead of resuming.
 */
export function startSessionRuntime(options: { freshSignIn?: boolean } = {}): void {
  const wired = ensureController();
  if (!wired) return;
  const { controller: c, activity: a } = wired;
  if (c.isEnding()) return;

  const shared = a.sharedActivityAt();
  c.start();
  if (!options.freshSignIn && shared > 0 && Date.now() - shared >= IDLE_TIMEOUT_MS) {
    void c.endSession('idle');
    return;
  }
  // A sign-in or a page load is user activity.
  a.markActivity(true);

  if (detach) return;
  const onInput = () => a.markActivity();
  const onWake = () => {
    if (document.visibilityState === 'hidden') return;
    void c.onWake();
  };
  const onHide = () => a.flush();
  ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onInput, { passive: true }));
  document.addEventListener('visibilitychange', onWake);
  window.addEventListener('focus', onWake);
  window.addEventListener('pageshow', onWake);
  window.addEventListener('online', onWake);
  window.addEventListener('pagehide', onHide);
  const timer = setInterval(() => void c.tick(), SESSION_TICK_MS);

  detach = () => {
    ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onInput));
    document.removeEventListener('visibilitychange', onWake);
    window.removeEventListener('focus', onWake);
    window.removeEventListener('pageshow', onWake);
    window.removeEventListener('online', onWake);
    window.removeEventListener('pagehide', onHide);
    clearInterval(timer);
    detach = null;
  };
}

export function stopSessionRuntime(): void {
  controller?.stop();
  detach?.();
}

/** The supabase-js SIGNED_OUT event, forwarded by AuthContext. */
export function notifySignedOut(): void {
  void controller?.onSignedOut();
}

/** Manual sign-out (Header, profile modal): revoke this device, clear, go to /login. */
export async function signOutThisDevice(): Promise<void> {
  const wired = ensureController();
  if (!wired) return;
  // A manual sign-out from a tab that never started (e.g. /signout page) still revokes.
  wired.controller.start();
  await wired.controller.endSession('manual');
}

/**
 * The `/api/*` fetch with the AUTH-002 guard and the one 401 path. Use for every client
 * call to an app route (phiFetch is a thin alias over it).
 */
export const authFetch: AuthFetch = createAuthFetch({
  controller: () => ensureController()?.controller ?? null,
  fetch: (...args) => fetch(...args),
  onProfileNotAuthorized: () => {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(AUTH_NOTICE_EVENT));
  },
  onPHINotEnabled: () => {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(PHI_NOT_ENABLED_EVENT));
  },
});
